import React, { useMemo } from 'react';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  IconButton,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  createFilterOptions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import useManageRoles from './hooks/useManageRoles';

function ManageRoles() {
  const {
    allRoles,
    selectedRole,
    allButtons,
    isEditMode,
    isEditForestMode,
    assignedForests,
    contracts,
    geofences,
    handleRoleChange,
    handleEditRole,
    handleEditForest,
    handleCheckboxChange,
    handleCancel,
    handleSave,
    isButtonChecked,
    getUniqueForests,
    selectedForestIds,
    setSelectedForestIds,
    handleRemoveAssignedForest,
    // Add Role
    isAddRoleModalOpen,
    newRoleName,
    setNewRoleName,
    addRoleError,
    handleOpenAddRole,
    handleCloseAddRole,
    handleAddRole,
  } = useManageRoles();

  // Create a Set of assigned siteAssessmentIDs to filter available contracts
  const assignedSiteIds = useMemo(() => new Set(assignedForests.map(f => f.siteAssessmentID)), [assignedForests]);

  const history = useHistory();
  const uniqueForests = getUniqueForests();

  const handleForestChange = selectedIds => {
    setSelectedForestIds(selectedIds);
  };

  const handleRemoveForest = siteAssessmentID => {
    const newIds = selectedForestIds.filter(id => id !== siteAssessmentID);
    setSelectedForestIds(newIds);
  };

  const isEditingDisabled = isEditMode || isEditForestMode;
  const showEditButtons = selectedRole && !isEditingDisabled;

  // Get available contracts (not assigned and not currently selected)
  const availableContracts = useMemo(() => {
    return geofences.filter(
      item =>
        !assignedSiteIds.has(item.siteAssessmentID) &&
        !selectedForestIds.includes(`${item.siteAssessmentID}_${item.geofenceType}`),
    );
  }, [geofences, assignedSiteIds, selectedForestIds]);

  // Map selected IDs to full forest objects
  const selectedForestObjects = useMemo(() => {
    return geofences.filter(g => selectedForestIds.includes(`${g.siteAssessmentID}_${g.geofenceType}`));
  }, [geofences, selectedForestIds]);

  const getGeofenceTypeLabel = type => {
    if (type === 'site') return 'Site';
    if (type === 'customer') return 'Customer';
    return type;
  };

  const forestFilter = useMemo(
    () =>
      createFilterOptions({
        stringify: option =>
          [
            option.siteAssessmentID,
            option.siteRef,
            option.contractUniqueID,
          ]
            .filter(Boolean)
            .join(' '),
      }),
    []
  );

  return (
    <div className="app-wrapper siteWrapper">
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" component="h2">
          Manage Role
        </Typography>

        <Box display={'flex'} gap={2}>
          <Button variant="contained" color="primary" onClick={handleOpenAddRole}>
            Add Role
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => history.push('/')}>
            Home
          </Button>
        </Box>
      </Box>

      {/* Role Dropdown + Edit Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: 600, mt: 2 }}>
        <FormControl fullWidth sx={{ maxWidth: 280 }}>
          <InputLabel>Select Role</InputLabel>
          <Select
            value={selectedRole?.id !== null ? selectedRole?.id : ''}
            label="Select Role"
            onChange={handleRoleChange}
            disabled={isEditingDisabled}>
            {allRoles.map(role => (
              <MenuItem key={role.id} value={role.id}>
                {role.rolename}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showEditButtons && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleEditRole}>
              Edit Role
            </Button>
            <Button variant="contained" onClick={handleEditForest}>
              Assign Forest
            </Button>
          </Box>
        )}
      </Box>

      {/* Role Permissions */}
      {allButtons.length > 0 && selectedRole !== null && isEditMode && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Role Permissions
          </Typography>
          <Grid container spacing={0.5}>
            {allButtons.map(button => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={button.buttonId}>
                <FormControlLabel
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': { fontSize: '12px' },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isButtonChecked(button)}
                      onChange={() => handleCheckboxChange(button.buttonId)}
                    />
                  }
                  label={button.buttonName || button.name}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Forest Assignment Section */}
      {isEditForestMode && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Assign Sites to Role
          </Typography>

          <Grid container spacing={3}>
            {/* Left side - Dropdown with chips */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    options={availableContracts}
                    value={selectedForestObjects}
                    disableCloseOnSelect
                    onChange={(event, newValue) => {
                      setSelectedForestIds(newValue.map(item => `${item.siteAssessmentID}_${item.geofenceType}`));
                    }}
                    getOptionLabel={option => `${option.siteAssessmentID} – ${option.contractUniqueID}`}
                    isOptionEqualToValue={(option, value) =>
                      option.siteAssessmentID === value.siteAssessmentID && option.geofenceType === value.geofenceType
                    }
                    filterOptions={forestFilter}
                    renderInput={params => (
                      <TextField {...params} label="Search & Select Forests" placeholder="Type Site ID or Contract ID..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={`${option.siteAssessmentID} – ${option.contractUniqueID}`}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2">
                            {option.siteAssessmentID} - {option.contractUniqueID}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {option.locationName} | {getGeofenceTypeLabel(option.geofenceType)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    ListboxProps={{
                      style: { maxHeight: 300 },
                    }}
                  />
                </FormControl>
              </Box>
            </Grid>

            {/* Right side - Currently Assigned forests */}
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Currently Assigned Forests ({assignedForests.length})
              </Typography>
              {assignedForests.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                  {assignedForests.map((forest, index) => (
                    <Box
                      key={`${forest.siteAssessmentID}-${forest.contractUniqueID}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.75,
                        px: 1,
                        mb: 0.5,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        '&:last-child': { mb: 0 },
                      }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="500" noWrap>
                          {forest.siteAssessmentID} – {forest.contractUniqueID}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {forest.locationName}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAssignedForest(forest)}
                        sx={{ ml: 1, '&:hover': { color: 'error.main' } }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #ddd',
                    borderRadius: 1,
                  }}>
                  <Typography variant="body2" color="text.secondary">
                    No forests currently assigned to this role
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Action Buttons */}
      {isEditingDisabled && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          {isEditMode && (
            <Button variant="contained" onClick={handleSave}>
              Save Permissions
            </Button>
          )}
          {isEditForestMode && !selectedForestIds.length && (
            <Button variant="contained" onClick={handleCancel}>
              Done
            </Button>
          )}
          {selectedForestIds.length > 0 && (
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save Selected Forests ({selectedForestIds.length})
            </Button>
          )}
        </Box>
      )}

      {/* Add Role Modal */}
      <Dialog open={isAddRoleModalOpen} onClose={handleCloseAddRole} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Role Name"
            value={newRoleName}
            onChange={e => {
              setNewRoleName(e.target.value);
            }}
            error={!!addRoleError}
            helperText={addRoleError}
            onKeyDown={e => {
              if (e.key === 'Enter') handleAddRole();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleCloseAddRole}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleAddRole}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ManageRoles;
